/**
 * visa/reminders.ts — pure smart-reminder and predictive-alert rules.
 *
 * Deterministic functions over the visa state and an explicit `now`; each
 * reminder has a deterministic id so the module can guarantee once-only
 * firing. Predictions are labeled as predictions — never as official facts.
 */

import {
  PROVENANCE_LABELS,
  type CountryKnowledge,
  type DocumentKind,
  type DocumentRef,
  type VisaRecord,
  type VisaReminder,
} from './models';

const DAY = 24 * 60 * 60 * 1000;

export function daysUntil(at: number, now: number): number {
  return Math.floor((at - now) / DAY);
}

/** Documents every roamer should have on file for an active visa. */
export const REQUIRED_DOCUMENT_KINDS: DocumentKind[] = ['passport', 'visa', 'insurance'];

export function missingDocuments(documents: DocumentRef[]): DocumentKind[] {
  return REQUIRED_DOCUMENT_KINDS.filter((kind) => !documents.some((d) => d.kind === kind));
}

export function extensionWindowOpen(visa: VisaRecord, now: number): boolean | null {
  if (!visa.extensionEligible || visa.extensionWindowDays == null) return visa.extensionEligible ? null : false;
  return daysUntil(visa.expiresAt, now) <= visa.extensionWindowDays && now < visa.expiresAt;
}

/** All reminders whose condition currently holds. The module de-duplicates. */
export function computeReminders(input: {
  visa: VisaRecord | null;
  passportExpiresAt: number | null;
  documents: DocumentRef[];
  now: number;
}): VisaReminder[] {
  const { visa, passportExpiresAt, documents, now } = input;
  const reminders: VisaReminder[] = [];

  if (visa) {
    const days = daysUntil(visa.expiresAt, now);
    const key = `${visa.country}-${visa.expiresAt}`;
    const thresholds: { limit: number; id: string; severity: VisaReminder['severity']; message: string }[] = [
      { limit: 30, id: `visa-30-${key}`, severity: 'info', message: `30 days remaining on your ${visa.country} visa.` },
      { limit: 14, id: `visa-14-${key}`, severity: 'warning', message: `14 days remaining on your ${visa.country} visa.` },
      { limit: 7, id: `visa-7-${key}`, severity: 'warning', message: `7 days remaining on your ${visa.country} visa.` },
      { limit: 1, id: `visa-1-${key}`, severity: 'urgent', message: `Your ${visa.country} visa expires tomorrow.` },
    ];
    for (const t of thresholds) {
      if (days <= t.limit && days >= 0) {
        reminders.push({ id: t.id, severity: t.severity, message: t.message, provenance: 'official', at: now });
      }
    }
    if (days < 0) {
      reminders.push({
        id: `visa-expired-${key}`, severity: 'urgent',
        message: `Your ${visa.country} visa has expired. Check official guidance immediately.`,
        provenance: 'official', at: now,
      });
    }
    if (extensionWindowOpen(visa, now) === true) {
      reminders.push({
        id: `ext-open-${key}`, severity: 'info',
        message: `Extension window now open for your ${visa.country} visa.`,
        provenance: 'official', at: now,
      });
    }
  }

  if (passportExpiresAt != null) {
    const days = daysUntil(passportExpiresAt, now);
    if (days <= 183 && days >= 0) {
      reminders.push({
        id: `passport-6mo-${passportExpiresAt}`, severity: 'warning',
        message: 'Your passport expires within 6 months — many countries refuse entry below this validity.',
        provenance: 'official', at: now,
      });
    }
    if (days < 0) {
      reminders.push({
        id: `passport-expired-${passportExpiresAt}`, severity: 'urgent',
        message: 'Your passport has expired.',
        provenance: 'official', at: now,
      });
    }
  }

  const missing = missingDocuments(documents);
  if (visa && missing.length > 0) {
    reminders.push({
      id: `docs-missing-${missing.join('-')}`, severity: 'warning',
      message: `Required documents missing: ${missing.join(', ')}.`,
      provenance: 'historical', at: now,
    });
  }

  return reminders;
}

/**
 * Predictive alerts — derived from recorded knowledge and the visa state,
 * always labeled as predictions or the provenance of their source item.
 */
export function computePredictiveAlerts(input: {
  visa: VisaRecord | null;
  knowledge: CountryKnowledge[];
  passportCountry: string | null;
  now: number;
}): VisaReminder[] {
  const { visa, knowledge, now } = input;
  const alerts: VisaReminder[] = [];

  if (visa) {
    const days = daysUntil(visa.expiresAt, now);
    if (days > 14 && days <= 45) {
      alerts.push({
        id: `predict-expiry-${visa.country}-${visa.expiresAt}`,
        severity: 'info',
        message: `${PROVENANCE_LABELS.prediction}: your visa expires in ${days} days — start planning the next step now.`,
        provenance: 'prediction', at: now,
      });
    }
  }

  // Future policy changes and nomad-visa eligibility surfaced FROM recorded
  // knowledge only — the module never invents government facts.
  for (const ck of knowledge) {
    for (const item of ck.futurePolicyChanges) {
      alerts.push({
        id: `predict-policy-${ck.country}-${item.recordedAt}`,
        severity: 'info',
        message: `${PROVENANCE_LABELS[item.provenance]}: ${ck.country} — ${item.text}`,
        provenance: item.provenance, at: now,
      });
    }
    for (const option of ck.visaOptions) {
      if (option.type === 'digitalNomad') {
        alerts.push({
          id: `predict-dnv-${ck.country}`,
          severity: 'info',
          message: `${PROVENANCE_LABELS[option.provenance]}: a Digital Nomad Visa may be available in ${ck.country} — ${option.description}`,
          provenance: option.provenance, at: now,
        });
      }
    }
  }

  return alerts;
}
